import React, { SyntheticEvent, useState } from "react";
import _ from "lodash";
import PageContentContainer from "../../../components/Container/PageContentContainer";
import OverviewActivityEntry from "./OverviewActivityEntry";
import { Box, Divider, Grid, Stack, Tab, Tabs } from "@mui/material";
import ActivityOverviewHeader from "../ActivityOverviewHeader/ActivityOverviewHeader";
import activityOverviewStyle from "./activityOverviewStyle";
import UnassignedActivities from "../UnassignedActivities/UnassignedActivities";
import { TabContext, TabPanel } from "@mui/lab";
import { FormatListBulleted, MailOutlined, PlaylistAdd, SvgIconComponent } from "@mui/icons-material";
import { userHasOneOfTheseRoles } from "../../../services/backofficeUserService";
import { Roles } from "../../../types/BackofficeUser";
import PagePagination from "../../../components/PagePagination/PagePagination";
import { useActivityOverviewWithFilters } from "../../../hooks/useActivityOverviewWithFilters";
import { useCurrentUser } from "../../../provider/CurrentUserProvider";
import { useBackofficeUser } from "../../../provider/BackofficeUserProvider";

const ActivityOverview = () => {
  const initialTab = localStorage.getItem("savedTab") || "activities";
  const [activeTab, setActiveTab] = useState(initialTab);
  const currentUser = useCurrentUser();
  const { agents } = useBackofficeUser();
  const {
    productFilter,
    setProductFilter,
    activityTypeFilter,
    setActivityTypeFilter,
    timespanFilter,
    setTimespanFilter,
    userFilter,
    setUserFilter,
    openOrDoneFilter,
    setOpenOrDoneFilter,
    activitiesResult,
    checkCaseActivitiesResult,
    mailAndDocumentActivitiesResult,
    page,
    setPage,
    updateActivities,
    refreshCounter,
    setRefreshCounter,
    currentTabTotalPages,
    overdueTotal,
    todayTotal,
  } = useActivityOverviewWithFilters({ activeTab, storageKey: "activityFiltersMobile" });

  const handleTabChange = (event: SyntheticEvent, newValue: string) => {
    setActiveTab(newValue);
    localStorage.setItem("savedTab", newValue);
  };

  const TabIcon = ({
    isLoading,
    icon: Icon,
    listLength,
  }: {
    isLoading: boolean;
    icon: SvgIconComponent;
    listLength?: number | null;
  }) => {
    return (
      <Grid container sx={{
        alignItems: "flex-start"
      }}>
        <Grid size={3} />
        <Grid size={6}>
          <Icon sx={activityOverviewStyle.icon} />
        </Grid>
        <Grid size={3}>
          {!isLoading && listLength && listLength > 0 && <span className={"MuiTabItem-tag"}>{listLength}</span>}
        </Grid>
      </Grid>
    );
  };

  return (
    <PageContentContainer>
      {userHasOneOfTheseRoles(currentUser, [Roles.accountmanager, Roles.admin, Roles.accounting]) && (
        <UnassignedActivities updateFilteredActivities={() => setRefreshCounter(refreshCounter + 1)} />
      )}
      <ActivityOverviewHeader
        overdueTotal={overdueTotal ?? 0}
        todayTotal={todayTotal ?? 0}
        hasActivityTypeFilter={activeTab === "activities"}
        setProductFilter={(data) => {
          updateActivities();
          setProductFilter(data);
        }}
        productFilter={productFilter}
        setActivityTypeFilter={(data: string) => {
          updateActivities();
          setActivityTypeFilter(data);
        }}
        activityTypeFilter={activityTypeFilter}
        userFilter={userFilter}
        setUserFilter={(data: string) => {
          updateActivities();
          setUserFilter(data);
        }}
        timespanFilter={timespanFilter}
        setTimespanFilter={(data: string) => {
          updateActivities();
          setTimespanFilter(data);
        }}
        openOrDoneFilter={openOrDoneFilter}
        setOpenOrDoneFilter={(data: string) => {
          updateActivities();
          setOpenOrDoneFilter(data);
        }}
        isLoading={activitiesResult.isLoading}
        agents={agents}
      />
      <TabContext value={activeTab}>
        <Tabs value={activeTab} sx={activityOverviewStyle.tabs} onChange={handleTabChange} variant={"fullWidth"}>
          <Tab
            icon={
              <TabIcon
                icon={FormatListBulleted}
                isLoading={activitiesResult.isLoading}
                listLength={activitiesResult.totalItems}
              />
            }
            value="activities"
          />
          <Tab
            icon={
              <TabIcon
                icon={MailOutlined}
                isLoading={mailAndDocumentActivitiesResult.isLoading}
                listLength={mailAndDocumentActivitiesResult.totalItems}
              />
            }
            value="inbox"
          />
          <Tab
            icon={
              <TabIcon
                icon={PlaylistAdd}
                isLoading={checkCaseActivitiesResult.isLoading}
                listLength={checkCaseActivitiesResult.totalItems}
              />
            }
            value="checkCases"
          />
        </Tabs>
        <TabPanel sx={activityOverviewStyle.tabPanel} value="activities">
          {activeTab === "activities" &&
            _.map(activitiesResult.activities, (activity, index) => (
              <React.Fragment key={activity.id}>
                <OverviewActivityEntry activity={activity} update={updateActivities} />
                {activitiesResult.activities && index !== activitiesResult.activities.length - 1 && (
                  <Box sx={activityOverviewStyle.divider}>
                    <Divider />
                  </Box>
                )}
              </React.Fragment>
            ))}
        </TabPanel>
        <TabPanel sx={activityOverviewStyle.tabPanel} value="inbox">
          {activeTab === "inbox" &&
            _.map(mailAndDocumentActivitiesResult.activities, (activity, index) => (
              <React.Fragment key={activity.id}>
                <OverviewActivityEntry activity={activity} update={updateActivities} />
                {mailAndDocumentActivitiesResult.activities &&
                  index !== mailAndDocumentActivitiesResult.activities.length - 1 && (
                    <Box sx={activityOverviewStyle.divider}>
                      <Divider />
                    </Box>
                  )}
              </React.Fragment>
            ))}
        </TabPanel>
        <TabPanel sx={activityOverviewStyle.tabPanel} value="checkCases">
          {activeTab === "checkCases" &&
            _.map(checkCaseActivitiesResult.activities, (activity, index) => (
              <React.Fragment key={activity.id}>
                <OverviewActivityEntry activity={activity} update={updateActivities} />
                {checkCaseActivitiesResult.activities && index !== checkCaseActivitiesResult.activities.length - 1 && (
                  <Box sx={activityOverviewStyle.divider}>
                    <Divider />
                  </Box>
                )}
              </React.Fragment>
            ))}
        </TabPanel>
        {!!currentTabTotalPages && currentTabTotalPages > 1 && (
          <Stack
            sx={{
              alignItems: "center",
            }}
          >
            <PagePagination
              currentPage={page}
              totalPages={currentTabTotalPages}
              handlePrevPage={() => setPage((page) => --page)}
              handleNextPage={() => setPage((page) => ++page)}
            />
          </Stack>
        )}
      </TabContext>
    </PageContentContainer>
  );
};

export default ActivityOverview;
